import { FunctionComponent } from "react";

import { confirm, UseFormMethods } from "@bps/fluent-ui";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import {
  RestrictionType,
  TenantDefaultUsersAuthenticationDto,
  TenantNetworkAuthenticationSettingsDto
} from "@libs/api/gateways/plt/plt-gateway.dtos";
import {
  useAddTenantDefaultUserAuth,
  useUpdateTenantDefaultUserAuth
} from "@libs/api/gateways/plt/plt-gateway.hooks";
import { Permissions } from "@libs/permissions/permissions.enum";

import {
  GlobalNetworkRestrictionsSection,
  GlobalNetworkRestrictionsValues
} from "../../_shared-blocks/network-authention-settings-section/GlobalNetworkRestrictionsSection";

interface TenantGlobalNetworkRestrictionsSectionProps {
  authSettings: TenantDefaultUsersAuthenticationDto | undefined;
  tenantId: string;
  networkSettings?: TenantNetworkAuthenticationSettingsDto[];
}

export const TenantGlobalNetworkRestrictionsSection: FunctionComponent<TenantGlobalNetworkRestrictionsSectionProps> = ({
  authSettings,
  tenantId,
  networkSettings
}) => {
  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltCatalogUserAuthenticationWrite
  );

  const { mutateAsync: addTenantAuth } = useAddTenantDefaultUserAuth();
  const { mutateAsync: updateTenantAuth } = useUpdateTenantDefaultUserAuth();

  const onSubmit = async (
    values: GlobalNetworkRestrictionsValues,
    actions: Pick<
      UseFormMethods<GlobalNetworkRestrictionsValues>,
      "reset" | "setValue"
    >
  ) => {
    // Show a warning message if the restriction type is "IP" and no enabled IP range is specified.
    if (
      values.restrictionType === RestrictionType.IP &&
      !networkSettings?.some(ip => ip.isActive)
    ) {
      const isConfirmed = await confirm({
        confirmButtonProps: {
          text: "Ok"
        },
        cancelButtonProps: {
          text: "Cancel"
        },
        dialogContentProps: {
          title: "No Enabled IP Range?",
          subText:
            "You have not specified an enabled IP range - this will block all access. Are you sure you want to continue?"
        }
      });
      if (!isConfirmed) return;
    }

    if (authSettings) {
      await updateTenantAuth({ ...authSettings, ...values });
    } else {
      await addTenantAuth({ ...values, id: tenantId });
    }
    actions.reset(values);
  };

  return (
    <GlobalNetworkRestrictionsSection
      hasWritePermission={!!hasWritePermission}
      initialValues={{
        restrictionType:
          authSettings?.restrictionType ?? RestrictionType.Worldwide
      }}
      onSubmit={onSubmit}
    />
  );
};
